import * as React from "react";
import { ChevronLeft, LayoutList } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import {
  apiErrorMessage,
  deleteBookingPage,
  deleteRollingLink,
  deletePoll,
  expireBookingPage,
  listBookingPages,
  listRollingLinks,
  listPolls,
  setBookingPageStatus,
  setRollingLinkStatus,
  setPollStatus,
} from "@/api/client";
import { BackBar } from "@/components/BackBar";
import { PageTitle } from "@/components/PageTitle";
import { BookingPageView, RollingLinkView, Poll } from "@/api/types";
import { ToastStack, useToasts } from "@/features/home/components/ToastStack";
import { cn } from "@/lib/utils";

import { AllLinksView } from "./AllLinksView";
import { BookingOffering, BookingOfferingSidebar } from "./BookingOfferingSidebar";
import { clearBuilderReturn, peekBuilderReturn } from "./builderReturn";
import { BookingPageBuilder } from "./BookingPageBuilder";
import { BookingPageDetail } from "./BookingPageDetail";
import { RollingLinkBuilder } from "./rolling/RollingLinkBuilder";
import { PollBuilder } from "./poll/PollBuilder";
import { PollDetail } from "./poll/PollDetail";
import { FOCUS_PAGE_PARAM, FOCUS_ROLLING_PARAM, FOCUS_SLOT_PARAM } from "./links";
import { useLinkActivity } from "./useLinkActivity";

/**
 * Booking pages, one screen: a builder that's always ready for a new page up top,
 * and the host's existing pages listed below (edit loads one into the builder;
 * copy / enable-disable / delete inline). No "new page" button — creating a page
 * is the obvious default, so we just present the builder. The public side an
 * invitee visits is {@link PublicBookingPage} at {@code /book/{slug}}.
 */
export function BookingPagesPage() {
  // A host who left mid-build to sort their calendar out (or their plan) comes back here. The
  // draft refills itself, but which builder held it lives only in this page's state, so without
  // the marker a rolling link would come back as an ad-hoc one on the All-bookings list.
  const returning = React.useMemo(() => peekBuilderReturn(), []);
  React.useEffect(() => {
    if (returning) clearBuilderReturn(); // one trip, one return
  }, [returning]);

  // Which offering the host is building. All three are live now: ad-hoc pages, rolling
  // appointments, and polls — and a returning host lands back on the one they left.
  const [offering, setOffering] = React.useState<BookingOffering>(returning?.offering ?? "adhoc");
  const [pages, setPages] = React.useState<BookingPageView[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState<BookingPageView | null>(null);
  // Who booked + the event journal for any link, fetched on demand and cached here so it survives
  // leaving the list for a builder and coming back.
  const {
    activity,
    load: loadActivity,
    loadMore: loadMoreActivity,
    invalidate: invalidateActivity,
  } = useLinkActivity();
  // "All bookings" is the page's home: it's what you land on, and picking an offering leaves it
  // for that builder. A ?page= deep link goes straight to that link's detail view instead.
  // A host coming back to their draft wants the builder, not the list.
  const [expanded, setExpanded] = React.useState(() => {
    if (returning) return false;
    const params = new URLSearchParams(window.location.search);
    // Either deep link lands on one link, not on the list — start collapsed so the list never
    // paints for a frame first and shoves the builder down as it is scrolled to.
    return !params.get(FOCUS_PAGE_PARAM) && !params.get(FOCUS_ROLLING_PARAM);
  });
  const { toasts, push, dismiss } = useToasts();
  const builderRef = React.useRef<HTMLDivElement>(null);
  // A deep link opens a builder that mounts in the same commit as the scroll, so wait for paint —
  // measuring first lands on the list that is on its way out. Reduced motion gets the jump.
  const scrollToBuilder = React.useCallback(() => {
    requestAnimationFrame(() => {
      const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      builderRef.current?.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
    });
  }, []);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      setPages(await listBookingPages());
    } catch {
      push({ tone: "failure", title: "Couldn't load your booking pages" });
    } finally {
      setLoading(false);
    }
  }, [push]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  // Rolling appointments sit alongside ad-hoc pages in the unified "All bookings" view.
  const [rollingLinks, setRollingLinks] = React.useState<RollingLinkView[]>([]);
  const [editingRollingLink, setEditingRollingLink] = React.useState<RollingLinkView | null>(null);
  const [rollingLinkBuilderKey, setRollingLinkBuilderKey] = React.useState(0);
  const reloadRollingLinks = React.useCallback(async () => {
    try {
      setRollingLinks(await listRollingLinks());
    } catch {
      /* non-fatal — the list just won't show rolling appointments */
    }
  }, []);
  React.useEffect(() => {
    void reloadRollingLinks();
  }, [reloadRollingLinks]);
  // Opening one by hand. Clears any lingering ?rolling= so the deep-link effect below can't
  // immediately drag the host back to the link they just navigated away from.
  const editRollingLink = (c: RollingLinkView) => {
    openedRollingByDeepLink.current = false;
    setOffering("rollingLink");
    setEditingRollingLink(c);
    setExpanded(false);
    clearFocus();
  };
  const newRollingLink = () => {
    openedRollingByDeepLink.current = false;
    setEditingRollingLink(null);
    setRollingLinkBuilderKey((k) => k + 1);
    clearFocus();
  };
  const copyAnyLink = async (path: string) => {
    const url = `${window.location.origin}${path.startsWith("/") ? path : `/${path}`}`;
    try {
      await navigator.clipboard.writeText(url);
      push({ tone: "success", title: "Link copied", body: url });
    } catch {
      push({ tone: "info", title: "Copy this link", body: url });
    }
  };
  const toggleRollingLink = async (c: RollingLinkView) => {
    try {
      await setRollingLinkStatus(c.id, c.status === "ACTIVE" ? "DISABLED" : "ACTIVE");
      invalidateActivity("rolling", c.id);
      await reloadRollingLinks();
    } catch (e) {
      push({ tone: "failure", title: "Couldn't update", body: apiErrorMessage(e) });
    }
  };
  // Polls: the third offering. Same shape as the rolling link block above — list, edit, toggle, remove —
  // with one extra state, the poll whose results are open.
  const [polls, setPolls] = React.useState<Poll[]>([]);
  const [editingPoll, setEditingPoll] = React.useState<Poll | null>(null);
  const [viewingPoll, setViewingPoll] = React.useState<Poll | null>(null);
  const [pollBuilderKey, setPollBuilderKey] = React.useState(0);
  const reloadPolls = React.useCallback(async () => {
    try {
      setPolls(await listPolls());
    } catch {
      /* non-fatal — the list just won't show polls */
    }
  }, []);
  React.useEffect(() => {
    void reloadPolls();
  }, [reloadPolls]);
  const newPoll = () => {
    setEditingPoll(null);
    setViewingPoll(null);
    setPollBuilderKey((k) => k + 1);
  };
  const openPoll = (p: Poll) => {
    setOffering("poll");
    setEditingPoll(null);
    setViewingPoll(p);
    setExpanded(false);
  };
  const editPoll = (p: Poll) => {
    setOffering("poll");
    setViewingPoll(null);
    setEditingPoll(p);
    setExpanded(false);
  };
  const togglePoll = async (p: Poll) => {
    try {
      await setPollStatus(p.id, p.status === "OPEN" ? "CLOSED" : "OPEN");
      invalidateActivity("poll", p.id);
      await reloadPolls();
    } catch (e) {
      push({ tone: "failure", title: "Couldn't update", body: apiErrorMessage(e) });
    }
  };
  const removePoll = async (p: Poll) => {
    // A confirmed, announced poll has a real meeting behind it, so say what removing it does.
    const announced = p.status === "CONFIRMED" && p.confirmation_sent_at;
    if (
      !window.confirm(
        announced
          ? `Remove "${p.title}"? Everyone who was told about the confirmed time gets an update ` +
              "that cancels it in their calendar."
          : `Remove "${p.title}"? Its link stops working and it's hidden from your list.`,
      )
    )
      return;
    try {
      await deletePoll(p.id);
      push({ tone: "success", title: "Poll removed" });
      if (editingPoll?.id === p.id) setEditingPoll(null);
      if (viewingPoll?.id === p.id) setViewingPoll(null);
      await reloadPolls();
    } catch (e) {
      push({ tone: "failure", title: "Couldn't remove", body: apiErrorMessage(e) });
    }
  };

  const removeRollingLink = async (c: RollingLinkView) => {
    if (
      !window.confirm(
        `Disable "${c.title}"? Its link stops taking bookings and it's hidden from your list. ` +
          "Existing appointments and their calendar invites are kept.",
      )
    )
      return;
    try {
      await deleteRollingLink(c.id);
      push({ tone: "success", title: "Rolling appointment disabled" });
      if (editingRollingLink?.id === c.id) setEditingRollingLink(null);
      await reloadRollingLinks();
    } catch (e) {
      push({ tone: "failure", title: "Couldn't disable", body: apiErrorMessage(e) });
    }
  };

  // Deep link from a calendar: ?page=<id>&slot=<ISO> opens that page's detail view and
  // focuses the booked slot. Wait for the pages to load, then open the matching one.
  const [searchParams, setSearchParams] = useSearchParams();
  const focusPageId = searchParams.get(FOCUS_PAGE_PARAM);
  const focusSlot = searchParams.get(FOCUS_SLOT_PARAM);
  // Deep link from the profile: ?rolling=<id> opens that rolling appointment in its builder. The
  // other half of the walk between a link and the availability pattern it owns.
  const focusRollingId = searchParams.get(FOCUS_ROLLING_PARAM);
  // True only while the detail view was opened BY a deep link, so a browser Back that drops the
  // params closes it and restores the builder. A manual card click sets it false, so Back leaves
  // that open. Without this, the params lingered and re-forced the page open — the "+ New" bug.
  const openedByDeepLink = React.useRef(false);
  // The same for ?rolling=. Kept apart from the one above: they answer different params, and one
  // ref would have a rolling deep link close itself the moment it noticed no ?page= was set.
  const openedRollingByDeepLink = React.useRef(false);

  // Strip the deep-link params (replace, so no extra history entry). Called whenever the user
  // manually leaves the detail view, so the effect below can't immediately re-open it.
  const clearFocus = React.useCallback(() => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete(FOCUS_PAGE_PARAM);
        p.delete(FOCUS_SLOT_PARAM);
        p.delete(FOCUS_ROLLING_PARAM);
        return p;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  React.useEffect(() => {
    if (focusPageId && pages.length > 0) {
      const page = pages.find((p) => p.id === focusPageId);
      if (page && editing?.id !== page.id) {
        setEditing(page);
        openedByDeepLink.current = true;
        setExpanded(false); // show the page detail, not the list
        scrollToBuilder();
      }
    } else if (!focusPageId && openedByDeepLink.current) {
      // Returned from a deep link (browser Back removed the params) — restore the builder.
      openedByDeepLink.current = false;
      setEditing(null);
    }
  }, [focusPageId, pages, editing?.id]);

  // The rolling half, mirroring the effect above. The list is fetched, so this waits for it
  // rather than racing it: until the link is in hand there is nothing to open.
  React.useEffect(() => {
    if (focusRollingId && rollingLinks.length > 0) {
      const link = rollingLinks.find((l) => l.id === focusRollingId);
      if (link && editingRollingLink?.id !== link.id) {
        // Inlined rather than calling editRollingLink, which clears the very param we're reading.
        setOffering("rollingLink");
        setEditingRollingLink(link);
        setExpanded(false);
        openedRollingByDeepLink.current = true;
        scrollToBuilder();
      }
    } else if (!focusRollingId && openedRollingByDeepLink.current) {
      // Browser Back dropped the param — leave the builder the way we found it.
      openedRollingByDeepLink.current = false;
      setEditingRollingLink(null);
    }
  }, [focusRollingId, rollingLinks, editingRollingLink?.id]);

  // A created page is immutable — "editing" opens its read-only detail view, not the builder.
  // A manual open isn't a deep link, and it clears any lingering ?page=&slot= so the deep-link
  // effect can't fight it.
  const viewPage = (page: BookingPageView) => {
    openedByDeepLink.current = false;
    setOffering("adhoc"); // an ad-hoc card can be clicked while the rollingLink builder is showing
    setExpanded(false);
    setEditing(page);
    clearFocus();
    scrollToBuilder();
  };

  // Back to a fresh builder — the "+ New" / "← New booking link" affordance. Clearing the
  // deep-link params is what actually fixes the bounce-back: without it the effect re-opened
  // the page the instant we set editing to null.
  const newPage = () => {
    openedByDeepLink.current = false;
    setEditing(null);
    clearFocus();
  };

  const copyLink = async (page: BookingPageView) => {
    const url = `${window.location.origin}${page.url_path}`;
    try {
      await navigator.clipboard.writeText(url);
      push({ tone: "success", title: "Link copied", body: url });
    } catch {
      push({ tone: "info", title: "Copy this link", body: url });
    }
  };

  const toggle = async (page: BookingPageView) => {
    const next = page.status === "ACTIVE" ? "DISABLED" : "ACTIVE";
    try {
      await setBookingPageStatus(page.id, next);
      // Keep the open detail view in sync (its Power button reads page.status).
      setEditing((cur) => (cur && cur.id === page.id ? { ...cur, status: next } : cur));
      invalidateActivity("adhoc", page.id); // the journal just gained an enabled/disabled entry
      await refresh();
    } catch (e) {
      push({ tone: "failure", title: "Couldn't change the page status", body: apiErrorMessage(e) });
    }
  };

  const expire = async (page: BookingPageView) => {
    if (
      !window.confirm(
        `Expire "${page.title}"? It stops taking bookings now, but stays in your list so you ` +
          `can still open it and its history. You can archive it later.`,
      )
    )
      return;
    try {
      const updated = await expireBookingPage(page.id);
      push({ tone: "success", title: "Booking link expired" });
      setEditing((cur) => (cur && cur.id === page.id ? { ...cur, status: updated.status } : cur));
      invalidateActivity("adhoc", page.id);
      await refresh();
    } catch (e) {
      push({ tone: "failure", title: "Couldn't expire the link", body: apiErrorMessage(e) });
    }
  };

  const remove = async (page: BookingPageView) => {
    if (
      !window.confirm(
        `Archive "${page.title}"? It's removed from your list and stops taking bookings. ` +
          `Its booking history is kept, but you can't reopen it.`,
      )
    )
      return;
    try {
      await deleteBookingPage(page.id);
      push({ tone: "success", title: "Booking page archived" });
      if (editing?.id === page.id) setEditing(null);
      await refresh();
    } catch (e) {
      push({ tone: "failure", title: "Couldn't archive the page", body: apiErrorMessage(e) });
    }
  };


  // "All bookings" is the page's home. Picking an offering leaves it for that builder.
  const openAllBookings = () => {
    setEditing(null);
    setEditingRollingLink(null);
    clearFocus();
    setExpanded(true);
  };
  const pickOffering = (next: BookingOffering) => {
    setOffering(next);
    setExpanded(false);
  };

  return (
    <>
      <main className="mx-auto max-w-none space-y-6 px-6 py-8">
        <div className="space-y-1">
          <PageTitle>Merge Availability with Preference &amp; have your booking link ready!</PageTitle>
        </div>

        <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[1fr_minmax(0,1140px)_1fr] lg:items-start lg:gap-4">
          {/* Left gutter: "All bookings" (the page's home) above the offering switcher. Sticky so
              both stay in view as you scroll the builder. The equal 1fr track on the right keeps
              the centre column centred. */}
          <div className="flex flex-col gap-5 lg:sticky lg:top-16 lg:self-start lg:justify-self-start">
            <div className="shrink-0 lg:w-64">
              {/* A destination, and it has to look like one. It used to carry the exact classes the
                  "Offerings" caption below it carries, so a link and a heading were the same thing to
                  look at. Sentence case, an active marker down the left, and the same hit area as the
                  offering rows underneath. */}
              <button
                type="button"
                onClick={openAllBookings}
                aria-current={expanded ? "page" : undefined}
                className={cn(
                  "flex w-full items-center gap-2 border-l-2 py-1.5 pl-2 pr-1 text-left text-sm font-semibold transition",
                  expanded
                    ? "border-brand-blue text-brand-blue"
                    : "border-transparent text-foreground hover:border-brand-blue/40 hover:text-brand-blue",
                )}
              >
                <LayoutList className="h-4 w-4 shrink-0" />
                All bookings
              </button>
            </div>
            <BookingOfferingSidebar value={offering} onChange={pickOffering} selected={!expanded} />
          </div>

          {/* Centre column: width-capped (matches the calendar) so Settings+Description
              and Calendar+Availability share one width, centred between the two equal
              gutters — the left gutter holds the rails, the right one stays empty. */}
          <div className="w-full min-w-0 space-y-6 lg:justify-self-center">
            {expanded ? (
              /* Unified "all links" — ad-hoc + rolling together, filter + card/list + config preview. */
              <AllLinksView
                pages={pages}
                rollingLinks={rollingLinks}
                polls={polls}
                loading={loading}
                activity={activity}
                onLoadActivity={loadActivity}
                onLoadMoreActivity={loadMoreActivity}
                onEditRollingLink={(id) => {
                  const c = rollingLinks.find((x) => x.id === id);
                  if (c) editRollingLink(c);
                }}
                onOpenAdhocDetail={(id) => {
                  const p = pages.find((x) => x.id === id);
                  if (p) viewPage(p);
                }}
                onOpenPollDetail={(id) => {
                  const p = polls.find((x) => x.id === id);
                  if (p) openPoll(p);
                }}
                onCopy={copyAnyLink}
                onToggleStatus={(kind, id) => {
                  if (kind === "adhoc") {
                    const p = pages.find((x) => x.id === id);
                    if (p) void toggle(p);
                  } else if (kind === "poll") {
                    const p = polls.find((x) => x.id === id);
                    if (p) void togglePoll(p);
                  } else {
                    const c = rollingLinks.find((x) => x.id === id);
                    if (c) void toggleRollingLink(c);
                  }
                }}
                onExpire={(id) => {
                  const p = pages.find((x) => x.id === id);
                  if (p) void expire(p);
                }}
                onArchive={(id) => {
                  const p = pages.find((x) => x.id === id);
                  if (p) void remove(p);
                }}
                onRemoveRollingLink={(id) => {
                  const c = rollingLinks.find((x) => x.id === id);
                  if (c) void removeRollingLink(c);
                }}
                onRemovePoll={(id) => {
                  const p = polls.find((x) => x.id === id);
                  if (p) void removePoll(p);
                }}
              />
            ) : offering === "adhoc" ? (
              <>
                {/* The builder — always ready for a new page, or editing one. */}
                <div ref={builderRef} className="space-y-2">
            {editing ? (
              <>
                {/* Only shows when we arrived here from a calendar (see BackBar) — returns
                    there, unwinding a multi-hop trail one step at a time. */}
                <BackBar current={`Booking: ${editing.title}`} />
                {/* Contextual breadcrumb: "Viewing X · ← Back to the booking draft" — the one
                    way back to the builder (which restores the in-progress session draft). */}
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                  <span className="font-semibold">
                    Viewing <span className="text-brand-blue">“{editing.title}”</span>
                  </span>
                  <span aria-hidden="true" className="text-muted-foreground">
                    ·
                  </span>
                  <button
                    type="button"
                    onClick={newPage}
                    className="inline-flex items-center gap-1 text-xs font-medium text-brand-blue hover:underline"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                    Back to the booking draft
                  </button>
                </div>
                <BookingPageDetail
                  page={editing}
                  focusSlot={focusPageId === editing.id ? focusSlot : null}
                  onCopyLink={() => copyLink(editing)}
                  onToggleStatus={() => toggle(editing)}
                  onExpire={() => expire(editing)}
                  onDelete={() => remove(editing)}
                  onBookingCancelled={(invitee) => {
                    push({
                      tone: "success",
                      title: "Booking cancelled",
                      body: `${invitee} has been told.`,
                    });
                    invalidateActivity("adhoc", editing.id);
                    void refresh();
                  }}
                  onBookingCancelFailed={(message) =>
                    push({ tone: "failure", title: "Couldn't cancel", body: message })
                  }
                />
              </>
            ) : (
              <BookingPageBuilder
                key="new"
                onSaved={(page) => {
                  push({ tone: "success", title: "Booking link created" });
                  setEditing(null);
                  void refresh();
                  void copyLink(page);
                  // Land on "All bookings" (which unmounts the builder) rather than sitting on
                  // the page just created — the draft is cleared, so returning gives a blank builder.
                  setExpanded(true);
                }}
                onCancel={() => setEditing(null)}
                onError={(message) => push({ tone: "info", title: "Almost there", body: message })}
              />
            )}
          </div>
              </>
            ) : offering === "rollingLink" ? (
              <div ref={builderRef} className="space-y-2">
                {/* Only shows when we arrived from an in-app deep link — the availability pattern
                    this link owns, or a calendar. Returns there, one hop at a time (see BackBar). */}
                <BackBar
                  current={editingRollingLink ? `Rolling: ${editingRollingLink.title}` : undefined}
                />
                <RollingLinkBuilder
                  key={editingRollingLink ? `edit-${editingRollingLink.id}` : `new-${rollingLinkBuilderKey}`}
                  existing={editingRollingLink}
                  onSaved={(rollingLink, created) => {
                    const url = `${window.location.origin}/${rollingLink.public_path}`;
                    if (created) navigator.clipboard?.writeText(url).catch(() => {});
                    push({
                      tone: "success",
                      title: created ? "Rolling appointment created. Link copied" : "Rolling appointment saved",
                      body: url,
                    });
                    newRollingLink();
                    void reloadRollingLinks();
                    // Land on "All bookings" rather than back on the just-saved appointment.
                    setExpanded(true);
                  }}
                  onCancel={newRollingLink}
                  onError={(message) => push({ tone: "info", title: "Almost there", body: message })}
                />
              </div>
            ) : viewingPoll ? (
              <PollDetail
                poll={viewingPoll}
                onEdit={editPoll}
                onChanged={(next) => {
                  setViewingPoll(next);
                  void reloadPolls();
                }}
                onCopyLink={copyAnyLink}
                onError={(message) => push({ tone: "failure", title: "Couldn't do that", body: message })}
                onNotify={(message) => push({ tone: "success", title: message })}
              />
            ) : (
              <PollBuilder
                key={editingPoll ? `edit-${editingPoll.id}` : `new-${pollBuilderKey}`}
                existing={editingPoll}
                onSaved={(poll) => {
                  const url = `${window.location.origin}/poll/${poll.slug}`;
                  if (!editingPoll) navigator.clipboard?.writeText(url).catch(() => {});
                  push({
                    tone: "success",
                    title: editingPoll ? "Poll saved" : "Poll created. Link copied",
                    body: url,
                  });
                  void reloadPolls();
                  // Land on the poll itself: unlike a booking link, a poll is something you now
                  // watch answers arrive on.
                  setEditingPoll(null);
                  setViewingPoll(poll);
                }}
                onCancel={newPoll}
                onError={(message) => push({ tone: "info", title: "Almost there", body: message })}
              />
            )}
          </div>
        </div>
      </main>
      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </>
  );
}
